import intl from "react-intl-universal";
import { normalizeSchemeCountry } from "./baasIdentitySchemes";

export const IDENTITY_DOC_LABEL_VARIANT = {
  DEFAULT: "default",
  /** BR CNH attached to a declared CPF / National ID */
  CPF_PROOF: "cpfProof",
  /** Primary identity document (e.g. US Driver's License from Veriff) */
  STANDALONE: "standalone",
};

export const IDENTITY_DOC_LABEL_ROLE = {
  /** Declaration block title (no front/back suffix). */
  DECLARATION: "declaration",
  /** Proof upload slot (front/back suffix). */
  PROOF_SIDE: "proofSide",
};

const DOC_TYPE_ALIASES = {
  DRIVERS_LICENSE: "DRIVERS_LICENSE",
  DRIVERS_LICENSE_BACK: "DRIVERS_LICENSE_BACK",
  PASSPORT: "PASSPORT",
  NATIONAL_ID: "NATIONAL_ID",
  SELFIE: "SELFIE",
  SSN: "SSN",
};

export const extractDocumentCountryCode = (doc) =>
  normalizeSchemeCountry(doc?.country_code || doc?.countryCode || "");

export const resolveCountryDisplayName = (countryCode, countryOptions = []) => {
  const cc = normalizeSchemeCountry(countryCode);
  if (!cc) return "";
  const fromList = countryOptions.find((entry) => entry.code === cc);
  if (fromList?.name) return fromList.name;
  const localeKey = `baas.entidades.create.country.${cc}`;
  const localized = intl.get(localeKey);
  return localized && localized !== localeKey ? localized : cc;
};

const getIntl = (key, fallbackKey, values) => {
  const primary = intl.get(key, values);
  if (primary && primary !== key) return primary;
  if (fallbackKey) {
    const fallback = intl.get(fallbackKey, values);
    if (fallback && fallback !== fallbackKey) return fallback;
  }
  return null;
};

const formatDriversLicenseLabel = ({
  side,
  countryCode,
  countryOptions,
  variant,
  labelRole = IDENTITY_DOC_LABEL_ROLE.PROOF_SIDE,
}) => {
  const cc = normalizeSchemeCountry(countryCode);
  const country = resolveCountryDisplayName(cc, countryOptions);
  const isBack = side === "back";
  const cpfProof = variant === IDENTITY_DOC_LABEL_VARIANT.CPF_PROOF;
  const isDeclaration = labelRole === IDENTITY_DOC_LABEL_ROLE.DECLARATION;

  if (isDeclaration && !isBack) {
    if (cc === "BR") {
      if (cpfProof) {
        return getIntl(
          "baas.entidades.detail.docType.driversLicenseBrDeclarationCpfProof",
          "baas.entidades.detail.docType.driversLicenseBrDeclaration",
        );
      }
      return getIntl(
        "baas.entidades.detail.docType.driversLicenseBrDeclaration",
        "baas.entidades.detail.docType.driversLicense",
      );
    }
    if (cc === "US") {
      return getIntl(
        "baas.entidades.detail.docType.driversLicenseUsDeclaration",
        "baas.entidades.detail.docType.driversLicenseGenericDeclaration",
      );
    }
    if (country) {
      return getIntl(
        "baas.entidades.detail.docType.driversLicenseCountryDeclaration",
        "baas.entidades.detail.docType.driversLicenseGenericDeclaration",
        { country },
      );
    }
    return getIntl(
      "baas.entidades.detail.docType.driversLicenseGenericDeclaration",
      "baas.entidades.detail.docType.driversLicense",
    );
  }

  if (cc === "BR") {
    if (cpfProof) {
      return getIntl(
        isBack
          ? "baas.entidades.detail.docType.driversLicenseBrBackCpfProof"
          : "baas.entidades.detail.docType.driversLicenseBrCpfProof",
        isBack
          ? "baas.entidades.detail.docType.driversLicenseBack"
          : "baas.entidades.detail.docType.driversLicense",
      );
    }
    return getIntl(
      isBack
        ? "baas.entidades.detail.docType.driversLicenseBrBack"
        : "baas.entidades.detail.docType.driversLicenseBr",
      isBack
        ? "baas.entidades.detail.docType.driversLicenseBack"
        : "baas.entidades.detail.docType.driversLicense",
    );
  }

  if (cc === "US") {
    return getIntl(
      isBack
        ? "baas.entidades.detail.docType.driversLicenseUsBack"
        : "baas.entidades.detail.docType.driversLicenseUs",
      isBack
        ? "baas.entidades.detail.docType.driversLicenseGenericBack"
        : "baas.entidades.detail.docType.driversLicenseGeneric",
    );
  }

  if (country) {
    return getIntl(
      isBack
        ? "baas.entidades.detail.docType.driversLicenseCountryBack"
        : "baas.entidades.detail.docType.driversLicenseCountry",
      isBack
        ? "baas.entidades.detail.docType.driversLicenseGenericBack"
        : "baas.entidades.detail.docType.driversLicenseGeneric",
      { country },
    );
  }

  return getIntl(
    isBack
      ? "baas.entidades.detail.docType.driversLicenseGenericBack"
      : "baas.entidades.detail.docType.driversLicenseGeneric",
    isBack
      ? "baas.entidades.detail.docType.driversLicenseBack"
      : "baas.entidades.detail.docType.driversLicense",
  );
};

/**
 * Localized label for an identity document type, reflecting issuing country when known.
 */
export const formatIdentityDocumentLabel = ({
  documentType,
  countryCode,
  countryOptions = [],
  variant = IDENTITY_DOC_LABEL_VARIANT.DEFAULT,
  labelRole = IDENTITY_DOC_LABEL_ROLE.PROOF_SIDE,
  identityVariant = null,
  doc = null,
}) => {
  const type = DOC_TYPE_ALIASES[String(documentType || "").toUpperCase()] ||
    String(documentType || "").toUpperCase();
  const cc = normalizeSchemeCountry(countryCode) || extractDocumentCountryCode(doc);
  const country = resolveCountryDisplayName(cc, countryOptions);
  const idVariant = String(identityVariant || "").trim().toLowerCase();

  switch (type) {
    case "DRIVERS_LICENSE":
      return formatDriversLicenseLabel({
        side: "front",
        countryCode: cc,
        countryOptions,
        variant,
        labelRole,
      });
    case "DRIVERS_LICENSE_BACK":
      return formatDriversLicenseLabel({
        side: "back",
        countryCode: cc,
        countryOptions,
        variant,
        labelRole,
      });
    case "PASSPORT":
      if (country) {
        return getIntl(
          "baas.entidades.detail.docType.passportCountry",
          "baas.entidades.detail.docType.passport",
          { country },
        );
      }
      return getIntl(
        "baas.entidades.detail.docType.passport",
        "baas.entidades.create.identity.passport",
      );
    case "NATIONAL_ID":
      if (idVariant === "rg") {
        return getIntl(
          "baas.entidades.detail.docType.nationalIdBrRg",
          "baas.entidades.detail.docType.nationalId",
        );
      }
      if (idVariant === "itin") {
        return getIntl(
          "baas.entidades.detail.docType.nationalIdItin",
          "baas.entidades.detail.docType.nationalId",
        );
      }
      if (cc === "BR") {
        return getIntl(
          "baas.entidades.detail.docType.nationalIdBr",
          "baas.entidades.create.identity.nationalId",
        );
      }
      if (country) {
        return getIntl(
          "baas.entidades.detail.docType.nationalIdCountry",
          "baas.entidades.create.identity.nationalId",
          { country },
        );
      }
      return getIntl(
        "baas.entidades.create.identity.nationalId",
        "baas.entidades.detail.docType.nationalId",
      );
    case "SELFIE":
      return getIntl(
        "baas.entidades.detail.docType.selfie",
        "baas.entidades.detail.docType.selfie",
      );
    case "SSN":
      return getIntl(
        "baas.entidades.detail.docType.ssn",
        "baas.entidades.detail.docType.ssn",
      );
    case "REGISTRATION_ID":
      if (idVariant === "crm") {
        return getIntl(
          "baas.entidades.detail.docType.registrationIdCrm",
          "baas.entidades.detail.docType.registrationId",
        );
      }
      return getIntl(
        "baas.entidades.detail.docType.registrationId",
        "baas.entidades.detail.docType.registrationId",
      );
    default:
      return type || "—";
  }
};

export const resolveIdentityDocumentLabelVariant = ({
  documentType,
  countryCode,
  isLinkedNationalIdProof = false,
}) => {
  const type = String(documentType || "").toUpperCase();
  if (type !== "DRIVERS_LICENSE" && type !== "DRIVERS_LICENSE_BACK") {
    return IDENTITY_DOC_LABEL_VARIANT.DEFAULT;
  }
  if (isLinkedNationalIdProof && normalizeSchemeCountry(countryCode) === "BR") {
    return IDENTITY_DOC_LABEL_VARIANT.CPF_PROOF;
  }
  return IDENTITY_DOC_LABEL_VARIANT.STANDALONE;
};

export const formatIdentityProofTitle = ({
  documentType,
  countryCode,
  countryOptions = [],
  identityVariant = null,
}) => {
  const type = String(documentType || "").toUpperCase();
  const country = resolveCountryDisplayName(countryCode, countryOptions);
  const idVariant = String(identityVariant || "").trim().toLowerCase();

  if (type === "PASSPORT") {
    if (country) {
      return getIntl(
        "baas.entidades.detail.passportProofTitleCountry",
        "baas.entidades.detail.passportProofTitle",
        { country },
      );
    }
    return getIntl(
      "baas.entidades.detail.passportProofTitle",
      "baas.entidades.detail.passportProofTitle",
    );
  }

  if (type === "REGISTRATION_ID" || idVariant === "crm") {
    return getIntl(
      "baas.entidades.detail.registrationIdProofTitle",
      "baas.entidades.detail.nationalIdProofTitleGeneric",
    );
  }

  if (type === "NATIONAL_ID" || type === "DRIVERS_LICENSE") {
    const cc = normalizeSchemeCountry(countryCode);
    if (type === "NATIONAL_ID" && idVariant === "rg") {
      return getIntl(
        "baas.entidades.detail.nationalIdRgProofTitle",
        "baas.entidades.detail.nationalIdProofTitleGeneric",
      );
    }
    if (cc === "BR") {
      return getIntl(
        "baas.entidades.detail.nationalIdProofTitle",
        "baas.entidades.detail.nationalIdProofTitleGeneric",
      );
    }
    if (cc === "US" && type === "DRIVERS_LICENSE") {
      return getIntl(
        "baas.entidades.detail.driversLicenseProofTitleUs",
        "baas.entidades.detail.nationalIdProofTitleGeneric",
      );
    }
    if (country) {
      return getIntl(
        type === "DRIVERS_LICENSE"
          ? "baas.entidades.detail.driversLicenseProofTitleCountry"
          : "baas.entidades.detail.nationalIdProofTitleCountry",
        "baas.entidades.detail.nationalIdProofTitleGeneric",
        { country },
      );
    }
  }

  return getIntl(
    "baas.entidades.detail.nationalIdProofTitleGeneric",
    "baas.entidades.detail.nationalIdProofTitle",
  );
};
